import React, { useEffect, useState } from 'react';
import { useAuth } from '../../commons/Auth';
import { useTranslation } from 'react-i18next';
import { useLoading } from '../../commons/Loading';
import { useAlert } from 'react-alert';
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import claimsAPIs from '../../lib/fetch/claimsAPIs';
import ClaimsPresentational from './ClaimsPresentational';
import axios from 'axios';

const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

const ClaimsAdmin = () => {
  const [{ newToken, newIsAdmin } = {}] = useAuth();
  const [t] = useTranslation();
  const { showLoading, hideLoading } = useLoading();
  const alert = useAlert();

  const [claims, setClaims] = useState([]);
  const [total, setTotal] = useState(0);
  const [events, setEvents] = useState([]);

  const [query, setQuery] = useQueryParams({
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
    offset: NumberParam,
    claimStartYear: NumberParam,
    claimStartMonth: NumberParam,
    branch: withDefault(StringParam, ''),
    policy_code: withDefault(StringParam, ''),
    insurance_code: withDefault(StringParam, ''),
    admin: withDefault(StringParam, ''),
    supplier: withDefault(StringParam, ''),
    status: withDefault(StringParam, ''),
    event: withDefault(StringParam, ''),
    appointedSupplier: withDefault(StringParam, ''),
    intern: withDefault(StringParam, ''),
  });

  const {
    limit,
    offset,
    claimStartYear,
    claimStartMonth,
    branch,
    policy_code,
    insurance_code,
    admin,
    supplier,
    active,
    status,
    event,
    appointedSupplier,
    intern,
  } = query;

  function onFilter(queryDiff) {
    if (
      (queryDiff.claimStartYear &&
        queryDiff.claimStartYear !== claimStartYear) || // first search
      (claimStartYear && queryDiff.claimStartYear === '') || // search deleted
      (queryDiff.claimStartMonth &&
        queryDiff.claimStartMonth !== claimStartMonth) || // first search
      (claimStartMonth && queryDiff.claimStartMonth === '') || // search deleted
      (queryDiff.branch && queryDiff.branch !== branch) || // first search
      (branch && queryDiff.branch === '') || // search deleted
      (queryDiff.policy_code && queryDiff.policy_code !== policy_code) || // first search
      (policy_code && queryDiff.policy_code === '') || // search deleted // search deleted
      (queryDiff.insurance_code &&
        queryDiff.insurance_code !== insurance_code) || // first search
      (insurance_code && queryDiff.insurance_code === '') || // search deleted
      (queryDiff.admin && queryDiff.admin !== admin) || // first search
      (admin && queryDiff.admin === '') || // search deleted
      (queryDiff.supplier && queryDiff.supplier !== supplier) || // first search
      (supplier && queryDiff.supplier === '') || // search deleted
      (queryDiff.active && queryDiff.active !== active) || // first search
      (active && queryDiff.active === '') || // search deleted
      (queryDiff.status && queryDiff.status !== status) || // first search
      (status && queryDiff.status === '') || // search deleted
      (queryDiff.event && queryDiff.event !== event) || // first search
      (event && queryDiff.event === '') || // search deleted
      (queryDiff.appointedSupplier &&
        queryDiff.appointedSupplier !== appointedSupplier) || // first search
      (appointedSupplier && queryDiff.appointedSupplier === '') || // search deleted
      (queryDiff.intern && queryDiff.intern !== intern) || // first search
      (intern && queryDiff.intern === '') || // search deleted
      (queryDiff.limit && queryDiff.limit !== limit)
    ) {
      queryDiff.offset = 0; // return to first page
    }

    setQuery(queryDiff);
  }

  const fetchClaims = async (query) => {
    showLoading();
    const { data, total: _total } = await claimsAPIs.find({
      newToken,
      query: { ...query },
    });
    hideLoading();

    setTotal(Number(_total || 0));
    setClaims(data);
  };

  const onClaimUpdate = async (data) => {
    showLoading();
    const { _id } = data;

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/claims/${_id}`,
      {
        ...data,
      },
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      const capsulateError = { error: response };
      const { error } = capsulateError;
      const { response: errorResponse, message: errorMessage } = error;
      const { data: errorData } = errorResponse || {};
      const { message: dataMessage } = errorData || {};
      alert.error(dataMessage || errorMessage || t('COMMON:GENERIC_ERROR'));
      return hideLoading();
    }

    alert.success(t('COMMON:SUCCESS_UPDATE'));
    fetchClaims();
  };

  const onClaimDelete = async (data) => {
    showLoading();

    const { _id } = data;

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/claims/${_id}`,
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      const capsulateError = { error: response };
      const { error } = capsulateError;
      const { response: errorResponse, message: errorMessage } = error;
      const { data: errorData } = errorResponse || {};
      const { message: dataMessage } = errorData || {};
      alert.error(dataMessage || errorMessage || t('COMMON:GENERIC_ERROR'));
      return hideLoading();
    }

    alert.success(t('COMMON:SUCCESS_DELETE'));
    fetchClaims();
  };

  //Per suggest nella modale di creazione fornitore incaricato
  const searchSuppliers = async (value) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/profiles/suppliers`,
      {
        headers: { Authorization: newToken },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [] } = response;
    return data;
  };

  useEffect(() => {
    const searchEvents = async (value) => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/claims/events`,
        {
          headers: { Authorization: newToken },
        }
      );

      if (response instanceof Error) {
        alert.error(t('COMMON:GENERIC_ERROR'));
        return [];
      }

      const { data = [] } = response;
      setEvents(data);

      return data;
    };
    if (events.length === 0) {
      searchEvents();
    }
    fetchClaims(query);
  }, [
    limit,
    offset,
    claimStartYear,
    claimStartMonth,
    branch,
    policy_code,
    insurance_code,
    admin,
    supplier,
    active,
    status,
    event,
    appointedSupplier,
    intern,
  ]);

  return (
    <ClaimsPresentational
      data={claims}
      events={events}
      query={query}
      total={total}
      onFilter={onFilter}
      searchSuppliers={searchSuppliers}
      onClaimUpdate={onClaimUpdate}
      onClaimDelete={onClaimDelete}
    />
  );
};

export default ClaimsAdmin;
